import { imageAccept } from "../../../utils/validateImage";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { useEffect, useState } from "react";
import { FaPlus } from "react-icons/fa6";
import Select from "react-select";
import { toast } from "react-toastify";
import apiClient from "../../../api/ApiClients";
import uploadIcon from "../../../assets/upload_icon.png";
import validateImage from "./../../../utils/validateImage";

const ServiceAssets = ({ service }) => {
  const [editable, setEditable] = useState(false);
  const [youTubeLinks, setYouTubeLinks] = useState([]);
  const [iframeLinks, setIframeLinks] = useState([]);
  const [assets, setAssets] = useState([]);
  const [removedAssets, setRemovedAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [photographers, setPhotographers] = useState([]);
  const [orderChanged, setOrderChanged] = useState(false);
  const [draggableMode, setDraggableMode] = useState(false);

  useEffect(() => {
    const fetchPhotographers = async () => {
      setLoading(true);
      try {
        const data = await apiClient.request("photographer/accepted", "GET");
        if (data && data.photographers) {
          setPhotographers(data.photographers);
        }
      } catch (error) {
        console.error(error);
      }
      setLoading(false);
    };
    fetchPhotographers();
    setYouTubeLinks(service.youTubeLinks || []);
    setIframeLinks(service.iframeLinks || []);
    setAssets(service.assets || []);
    setOrderChanged(false);
  }, [service]);

  const handleYouTubeInputChange = (index, value) => {
    if (editable) {
      const newLinks = [...youTubeLinks];
      newLinks[index] = value;
      setYouTubeLinks(newLinks);
    }
  };

  const handleIframeInputChange = (index, value) => {
    if (editable) {
      const newLinks = [...iframeLinks];
      newLinks[index] = value;
      setIframeLinks(newLinks);
    }
  };

  const handleAddYouTubeLink = () => {
    if (editable) {
      setYouTubeLinks([...youTubeLinks, ""]);
    }
  };

  const handleAddIframeLink = () => {
    if (editable) {
      setIframeLinks([...iframeLinks, ""]);
    }
  };

  const handleRemoveYouTubeLink = (index) => {
    if (editable) {
      const newLinks = [...youTubeLinks];
      newLinks.splice(index, 1);
      setYouTubeLinks(newLinks);
    }
  };

  const handleRemoveIframeLink = (index) => {
    if (editable) {
      const newLinks = [...iframeLinks];
      newLinks.splice(index, 1);
      setIframeLinks(newLinks);
    }
  };

  const handleFileChange = async (fileList) => {
    if (editable) {
      const fileArray = Array.from(fileList);
      for (const file of fileArray) {
        const { isValid, error } = await validateImage(file);
        if (!isValid) {
          toast.error(error);
          continue;
        }
        const newAsset = {
          photographerId: null,
          imageUrl: URL.createObjectURL(file),
          file,
        };
        setAssets((prevAssets) => [...prevAssets, newAsset]);
      }
    }
  };

  const handleRemoveAsset = (index) => {
    if (editable) {
      const assetToRemove = assets[index];
      setAssets((prevAssets) => prevAssets.filter((_, i) => i !== index));
      if (assetToRemove.imageUrl && !assetToRemove.file) {
        setRemovedAssets((prev) => [...prev, assetToRemove.imageUrl]);
      }
      setOrderChanged(true);
    }
  };

  const handlePhotographerChange = (index, selectedOption) => {
    if (editable) {
      const updatedAssets = [...assets];
      updatedAssets[index].photographerId = selectedOption
        ? selectedOption.value
        : null;
      setAssets(updatedAssets);
      setOrderChanged(true);
    }
  };
  // Drag and drop reorder handler
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(assets);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    setAssets(reordered);
    setOrderChanged(true);
  };

  // Save order API call
  const handleSaveOrder = async () => {
    const assetsOrder = assets
      .filter((asset) => asset.imageUrl && !asset.file)
      .map((asset) => asset.imageUrl);
    if (!assetsOrder.length) {
      toast.error("No existing assets to reorder");
      return;
    }
    try {
      const data = await apiClient.request(
        `service/reorder-assets/${service._id}`,
        "POST",
        { assetsOrder }
      );
      if (data) {
        toast.success("Asset order updated successfully");
        setOrderChanged(false);
      }
    } catch (error) {
      toast.error("Failed to update asset order");
      console.error(error);
    }
  };

  const handleSave = async (status) => {
    const youtubeUrlValidation =
      /^(http(s)?:\/\/)?((w){3}.)?youtu(be|.be)?(\.com)?\/.+/;

    if (youTubeLinks.length > 15) {
      toast.error("You can only add up to 15 YouTube links");
      return;
    }

    for (let i = 0; i < youTubeLinks.length; i++) {
      if (!youtubeUrlValidation.test(youTubeLinks[i])) {
        toast.error(`Invalid YouTube link at position ${i + 1}`);
        return;
      }
    }

    const formData = new FormData();
    formData.append("youTubeLinks", JSON.stringify(youTubeLinks));
    formData.append("iframeLinks", JSON.stringify(iframeLinks));
    assets.forEach((asset) => {
      if (asset.file) {
        formData.append("images", asset.file, asset.file.name);
      }
    });
    const filteredAssets = assets.filter((asset) => !asset.file);
    formData.append("assets", JSON.stringify(filteredAssets));
    removedAssets.forEach((url) => formData.append("removedAssets[]", url));
    formData.append("status", status);

    try {
      const data = await apiClient.request(
        `service/update/${service._id}`,
        "PUT",
        formData,
        true
      );

      if (data) {
        toast.success("Service updated successfully");
      }
    } catch (error) {
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "2.5rem",
        }}
      >
        <h2>Loading...</h2>
      </div>
    );
  }

  const photographerOptions = photographers.map((photographer) => ({
    value: photographer._id,
    label: photographer.name,
  }));

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "2.5rem",
        }}
      >
        {editable ? (
          <>
            <button
              className="button-edit"
              style={{ width: "10rem" }}
              onClick={() => setEditable(false)}
            >
              Cancel
            </button>
            <div style={{ display: "flex", gap: 0 }}>
              <button
                className="button-submit"
                style={{ width: "10rem" }}
                onClick={() => handleSave("active")}
              >
                Save
              </button>
              {!draggableMode && (
                <button
                  className="button-edit"
                  style={{ marginLeft: "1rem" }}
                  onClick={() => setDraggableMode(true)}
                >
                  Edit Draggable
                </button>
              )}
              {draggableMode && (
                <>
                  <button
                    className="button-submit"
                    style={{ width: "10rem", marginLeft: "1rem" }}
                    onClick={handleSaveOrder}
                    disabled={!orderChanged}
                  >
                    Save Order
                  </button>
                  <button
                    className="button-edit"
                    style={{ marginLeft: "1rem" }}
                    onClick={() => setDraggableMode(false)}
                  >
                    Cancel Draggable
                  </button>
                </>
              )}
            </div>
          </>
        ) : (
          <button
            className="button-edit"
            style={{ width: "10rem" }}
            onClick={() => setEditable(true)}
          >
            Edit
          </button>
        )}
      </div>
      <div style={{ padding: "0 15rem" }}>
        <h4>Upload Images</h4>
        <div
          className="upload-container"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            handleFileChange(e.dataTransfer.files);
          }}
        >
          <img src={uploadIcon} alt="Upload Icon" className="upload-icon" />
          <p className="upload-text">
            Drag and drop or
            <label htmlFor="fileInput" className="browse-label">
              Browse
            </label>
            file.
          </p>
          <input
            type="file"
            disabled={!editable}
            id="fileInput"
            style={{ display: "none" }}
            accept={imageAccept}
            multiple
            onChange={(e) => handleFileChange(e.target.files)}
          />
          <p>Supported formats: JPG, PNG, WebP and AVIF</p>
        </div>
        {/* Images Section */}
        {draggableMode ? (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="assets-droppable" direction="vertical">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                    marginTop: "30px",
                  }}
                >
                  {assets.map((asset, index) => (
                    <Draggable
                      key={index}
                      draggableId={"asset-" + index}
                      index={index}
                      isDragDisabled={!draggableMode}
                    >
                      {(dragProvided, dragSnapshot) => (
                        <div
                          className="image-container"
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          style={{
                            ...dragProvided.draggableProps.style,
                            boxShadow: dragSnapshot.isDragging
                              ? "0 0 10px #aaa"
                              : undefined,
                            margin: "0 0 10px 0",
                            width: "fit-content",
                          }}
                        >
                          <img
                            src={asset.imageUrl}
                            alt="Uploaded"
                            className="uploaded-image"
                          />
                          {editable && (
                            <>
                              <button
                                onClick={() => handleRemoveAsset(index)}
                                className="remove-button"
                              >
                                ×
                              </button>
                              <Select
                                options={photographerOptions}
                                value={photographerOptions.find(
                                  (option) =>
                                    option.value === asset.photographerId
                                )}
                                onChange={(selectedOption) =>
                                  handlePhotographerChange(
                                    index,
                                    selectedOption
                                  )
                                }
                                placeholder="Select Photographer"
                                isClearable
                              />
                            </>
                          )}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <div
            style={{
              display: "flex",
              gap: "1rem",
              flexWrap: "wrap",
              marginTop: "30px",
            }}
          >
            {assets.map((asset, index) => (
              <div className="image-container" key={index}>
                <img
                  src={asset.imageUrl}
                  alt="Uploaded"
                  className="uploaded-image"
                />
                {editable && (
                  <>
                    <button
                      onClick={() => handleRemoveAsset(index)}
                      className="remove-button"
                    >
                      ×
                    </button>
                    <Select
                      options={photographerOptions}
                      value={photographerOptions.find(
                        (option) => option.value === asset.photographerId
                      )}
                      onChange={(selectedOption) =>
                        handlePhotographerChange(index, selectedOption)
                      }
                      placeholder="Select Photographer"
                      isClearable
                    />
                  </>
                )}
              </div>
            ))}
          </div>
        )}
        <div style={{ padding: "1rem 0" }}>
          <hr />
        </div>
        <h4>Enter YouTube Links</h4>
        {youTubeLinks.map((link, index) => (
          <div className="mb-3" key={index}>
            <div className="input-group">
              <input
                type="text"
                className="form-control py-3"
                required
                placeholder="Enter YouTube link"
                value={link}
                disabled={!editable}
                onChange={(e) =>
                  handleYouTubeInputChange(index, e.target.value)
                }
              />
            </div>
            {editable && (
              <h2
                style={{
                  color: "var(--orange-color)",
                  fontSize: "18px",
                  cursor: "pointer",
                  textAlign: "end",
                  marginTop: "10px",
                }}
                onClick={() => handleRemoveYouTubeLink(index)}
              >
                Remove
              </h2>
            )}
          </div>
        ))}
        {editable && (
          <h2 className="mt-3 add-more" onClick={handleAddYouTubeLink}>
            <FaPlus style={{ marginBottom: "4px" }} /> ADD MORE
          </h2>
        )}
        <div style={{ padding: "1rem 0" }}>
          <hr />
        </div>
        <h4>Enter Iframe Links</h4>
        {iframeLinks.map((link, index) => (
          <div className="mb-3" key={index}>
            <div className="input-group">
              <input
                type="text"
                className="form-control py-3"
                required
                placeholder="Enter iframe link"
                value={link}
                disabled={!editable}
                onChange={(e) => handleIframeInputChange(index, e.target.value)}
              />
            </div>
            {editable && (
              <h2
                style={{
                  color: "var(--orange-color)",
                  fontSize: "18px",
                  cursor: "pointer",
                  textAlign: "end",
                  marginTop: "10px",
                }}
                onClick={() => handleRemoveIframeLink(index)}
              >
                Remove
              </h2>
            )}
          </div>
        ))}
        {editable && (
          <h2 className="mt-3 add-more" onClick={handleAddIframeLink}>
            <FaPlus style={{ marginBottom: "4px" }} /> ADD MORE
          </h2>
        )}
      </div>
    </>
  );
};

export default ServiceAssets;
